import { useState, useEffect, useMemo } from "react";
import { Shield, RefreshCw, Database, Users as UsersIcon, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BACKEND_URL, ADMIN_EMAILS, getAuthHeaders, computeStats,
  formatDate, getUserTagType, TAG_CONFIG,
} from "../components/admin/adminHelpers";
import { DashboardStatsRow } from "../components/admin/DashboardStatsRow";
import { UserFilterBar } from "../components/admin/UserFilterBar";
import { UserManagementTable } from "../components/admin/UserManagementTable";
import { MobileUserCardList } from "../components/admin/MobileUserCardList";
import { UserDetailPanel } from "../components/admin/UserDetailPanel";
import { WaitlistPanel } from "../components/admin/WaitlistPanel";

export const Admin = () => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ phone: "", received_trial_kit: false });
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({
    hasPhone: "all", receivedKit: "all", period: "all", customerType: "all",
  });
  const [articleReads, setArticleReads] = useState(0);
  const [activeTab, setActiveTab] = useState("users");
  const [waitlist, setWaitlist] = useState([]);

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include", headers: getAuthHeaders(),
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          if (ADMIN_EMAILS.includes(userData.email)) {
            setIsAuthorized(true);
            fetchUsers();
            fetchArticleReads();
            fetchWaitlist();
          } else {
            setIsAuthorized(false);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        credentials: "include", headers: getAuthHeaders(),
      });
      if (response.ok) setUsers(await response.json());
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArticleReads = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/articles/views`, {
        credentials: "include", headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        const total = Object.values(data).reduce((sum, n) => sum + (Number(n) || 0), 0);
        setArticleReads(total);
      }
    } catch (error) {
      console.error("Failed to fetch article reads:", error);
    }
  };

  const fetchWaitlist = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/waitlist`, {
        credentials: "include", headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setWaitlist(Array.isArray(data) ? data : (data.entries || []));
      }
    } catch (error) {
      console.error("Failed to fetch waitlist:", error);
    }
  };

  const deleteWaitlistEntry = async (waitlistId) => {
    if (!window.confirm("למחוק את הנרשם מרשימת ההמתנה?")) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/waitlist/${waitlistId}`, {
        method: "DELETE", credentials: "include", headers: getAuthHeaders(),
      });
      if (response.ok) {
        setWaitlist((prev) => prev.filter((e) => e.waitlist_id !== waitlistId));
      }
    } catch (error) {
      console.error("Failed to delete waitlist entry:", error);
    }
  };

  const stats = useMemo(() => ({ ...computeStats(users), articleReads }), [users, articleReads]);

  // Filter & sort
  const filteredUsers = useMemo(() => {
    return users
      .filter(u => {
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          if (!(u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.includes(q))) return false;
        }
        if (filters.hasPhone === "yes" && !u.phone) return false;
        if (filters.hasPhone === "no" && u.phone) return false;
        if (filters.receivedKit === "yes" && !u.received_trial_kit) return false;
        if (filters.receivedKit === "no" && u.received_trial_kit) return false;
        if (filters.customerType === "new") {
          if (new Date(u.created_at) < new Date(Date.now() - 7 * 86400000)) return false;
        }
        if (filters.customerType === "vip" && (u.login_count || 0) <= 5) return false;
        if (filters.customerType === "no_purchase" && (u.coupon_used || u.purchased_pack)) return false;
        if (filters.period !== "all") {
          const createdAt = new Date(u.created_at);
          const today = new Date(); today.setHours(0,0,0,0);
          if (filters.period === "today" && createdAt < today) return false;
          if (filters.period === "week" && createdAt < new Date(today.getTime() - 7 * 86400000)) return false;
          if (filters.period === "month" && createdAt < new Date(today.getTime() - 30 * 86400000)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        let aVal = a[sortField], bVal = b[sortField];
        if (sortField === "created_at" || sortField === "last_login") {
          aVal = new Date(aVal || 0); bVal = new Date(bVal || 0);
        } else {
          aVal = (aVal || "").toString().toLowerCase();
          bVal = (bVal || "").toString().toLowerCase();
        }
        return sortDirection === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });
  }, [users, searchQuery, filters, sortField, sortDirection]);

  // Handlers
  const handleLogin = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/admin")}`;
  };

  const startEditing = (u) => {
    setEditingUser(u.user_id);
    setEditForm({ phone: u.phone || "", received_trial_kit: u.received_trial_kit || false });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({ phone: "", received_trial_kit: false });
  };

  const saveUser = async (userId) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT', credentials: "include", headers: getAuthHeaders(),
        body: JSON.stringify(editForm),
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(users.map(u => u.user_id === userId ? updatedUser : u));
        setEditingUser(null);
        // Update selected user if open
        if (selectedUser?.user_id === userId) setSelectedUser(updatedUser);
      } else {
        const errorText = await response.text();
        console.error("Update failed:", response.status, errorText);
        alert(`שגיאה בשמירה: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      alert("שגיאה בשמירה — בדקו את החיבור");
    } finally {
      setIsSaving(false);
    }
  };

  const quickToggleTrialKit = async (userId, value) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT', credentials: "include", headers: getAuthHeaders(),
        body: JSON.stringify({ received_trial_kit: value }),
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(users.map(u => u.user_id === userId ? updatedUser : u));
        if (selectedUser?.user_id === userId) setSelectedUser(updatedUser);
      }
    } catch (error) {
      console.error("Failed to toggle trial kit:", error);
    }
  };

  const markWhatsAppContacted = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT', credentials: "include", headers: getAuthHeaders(),
        body: JSON.stringify({ whatsapp_contacted: true }),
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(users.map(u => u.user_id === userId ? updatedUser : u));
      }
    } catch (error) { console.error("Failed to mark as contacted:", error); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDirection(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDirection("desc"); }
  };

  // Export functions
  const exportToCSV = (onlyNoPurchase = false) => {
    let exportUsers = onlyNoPurchase ? filteredUsers.filter(u => !u.coupon_used && !u.purchased_pack) : filteredUsers;
    const headers = ["מספר","שם","אימייל","טלפון","סטטוס","קיבל ערכה","נוצר קשר","תאריך הרשמה","כניסה אחרונה"];
    const rows = exportUsers.map((u, i) => [
      i+1, u.name||"ללא שם", u.email, u.phone||"—",
      TAG_CONFIG[getUserTagType(u)].label,
      u.received_trial_kit?"כן":"לא", u.whatsapp_contacted?"כן":"לא",
      formatDate(u.created_at), formatDate(u.last_login),
    ]);
    const BOM = '\uFEFF';
    const csv = BOM + [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `mart_users${onlyNoPurchase?'_no_purchase':''}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToJSON = () => {
    const blob = new Blob([JSON.stringify(filteredUsers, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `mart_users_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  // --- Render ---
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] dark:bg-[#0f1510] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-7 w-7 animate-spin text-[#4A5D43] mx-auto mb-4" />
          <p className="font-sans text-sm text-[#5C5C5C]">טוען...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] dark:bg-[#0f1510] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-sm bg-[#2C3329]/5 flex items-center justify-center mx-auto mb-5">
            <Shield className="h-8 w-8 text-[#4A5D43]" />
          </div>
          <h1 className="font-serif text-2xl text-[#2C3329] dark:text-white mb-3">ממשק ניהול</h1>
          <p className="font-sans text-sm text-[#5C5C5C] dark:text-[#9A9790] mb-6">יש להתחבר כדי לגשת לממשק הניהול</p>
          <Button data-testid="admin-login-btn" onClick={handleLogin} className="bg-[#2C3329] hover:bg-[#4A5D43] text-[#FAF8F5] rounded-sm px-8 py-2.5 transition-colors">
            התחברות עם Google
          </Button>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] dark:bg-[#0f1510] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-sm bg-[#C67D63]/10 flex items-center justify-center mx-auto mb-5">
            <Shield className="h-8 w-8 text-[#C67D63]" />
          </div>
          <h1 className="font-serif text-2xl text-[#2C3329] dark:text-white mb-3">אין הרשאה</h1>
          <p className="font-sans text-sm text-[#5C5C5C] dark:text-[#9A9790] mb-2">אין לך הרשאות גישה לממשק הניהול.</p>
          <p className="font-sans text-xs text-[#A3A3A3]">מחובר כ: {user.email}</p>
        </div>
      </div>
    );
  }

  const sharedHandlers = {
    editingUser, editForm, setEditForm, isSaving,
    onStartEdit: startEditing,
    onSaveEdit: saveUser,
    onCancelEdit: cancelEditing,
    onToggleTrialKit: quickToggleTrialKit,
    onMarkWhatsAppContacted: markWhatsAppContacted,
    onSelectUser: setSelectedUser,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5] dark:bg-[#0f1510] pt-20 pb-12 px-4 md:px-6 lg:px-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-[#2C3329] dark:text-white flex items-center gap-3">
              <Shield className="h-6 w-6 text-[#4A5D43]" />
              ממשק ניהול
            </h1>
            <p className="font-sans text-sm text-[#5C5C5C] dark:text-[#9A9790] mt-1">שלום, {user.name}</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-[#6B8E6B]/5 rounded-sm border border-[#6B8E6B]/15">
            <Database className="h-3.5 w-3.5 text-[#6B8E6B]" />
            <span className="font-sans text-xs text-[#6B8E6B]">מחובר</span>
            <div className="w-1.5 h-1.5 rounded-full bg-[#6B8E6B] animate-pulse" />
          </div>
        </div>

        {/* Stats */}
        <DashboardStatsRow stats={stats} />

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 border-b border-[#2C3329]/10 dark:border-[#2a3a2a]">
          {[
            { key: "users", label: `משתמשים (${users.length})`, icon: UsersIcon },
            { key: "waitlist", label: `רשימת המתנה (${waitlist.length})`, icon: ClipboardList },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                data-testid={`admin-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium -mb-px border-b-2 transition-colors ${
                  active
                    ? "border-[#4A5D43] text-[#2C3329] dark:text-white"
                    : "border-transparent text-[#9A9790] hover:text-[#5C5C5C]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === "users" && (
          <>
            {/* Filters */}
            <UserFilterBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filters={filters}
              setFilters={setFilters}
              filteredCount={filteredUsers.length}
              totalCount={users.length}
              onExportCSV={() => exportToCSV(false)}
              onExportNoPurchaseCSV={() => exportToCSV(true)}
              onExportJSON={exportToJSON}
            />

            {/* Desktop Table */}
            <div className="hidden md:block">
              <UserManagementTable
                users={filteredUsers}
                sortField={sortField}
                sortDirection={sortDirection}
                onToggleSort={toggleSort}
                {...sharedHandlers}
              />
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden">
              <MobileUserCardList users={filteredUsers} {...sharedHandlers} />
            </div>

            {/* User Detail Panel */}
            <UserDetailPanel
              user={selectedUser}
              isOpen={!!selectedUser}
              onClose={() => setSelectedUser(null)}
              onUserUpdated={(updated) => {
                setUsers(users.map(u => u.user_id === updated.user_id ? updated : u));
                setSelectedUser(updated);
              }}
            />
          </>
        )}

        {activeTab === "waitlist" && (
          <WaitlistPanel entries={waitlist} onDelete={deleteWaitlistEntry} />
        )}
      </div>
    </div>
  );
};

export default Admin;
